import { saveAs } from 'file-saver'
import { filetypeinfo } from 'magic-bytes.js'
import { useEffect } from 'haunted';
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { formatBytes } from '../functions/format_bytes';
import { LitHauntedElement } from '../mixins/lit-haunted-element';
import type { GuessedFile } from 'magic-bytes.js/dist/model/tree';

export async function DataURIToBlob(dataUri: string): Promise<Blob | undefined> {
  try {
    const res = await fetch(dataUri);
    let blob = await res.blob();
    const magic = await GetMagicNumber(blob);
    
    /**
     * Detect weird scenario where the images in the Northwind service are corrupted
     * See: https://blogs.sap.com/2017/02/08/displaying-images-in-sapui5-received-from-the-northwind-odata-service/
     */
    if (magic === '151c2f0') {
      blob = blob.slice(78)
    }

    // Attempt to get the real mimetype
    const fileType = await GuessMimeType(blob);
    if (fileType?.mime) {
      blob = new Blob([ blob ], { 
        type: fileType.mime,
      })
    }
    //console.log('Successfully decoded blob! Magic:', magic, blob);
    return blob;
  }
  catch (err) {
    //console.error('Failed to decode blob: ', err);
    return undefined;
  }
}

export async function GuessMimeType(blob: Blob) {
  try {
    const array = await blob.stream().getReader().read()
    if (array.value) {
      const guesses = filetypeinfo(array.value);
      return guesses.find(() => true);
    }
  }
  catch {}
  return undefined;
}

export async function GetMagicNumber(blob: Blob): Promise<string | undefined> {
  try {
    const header = blob.slice(0, 4);
    const array = await header.stream().getReader().read()
    let magic = '';
    if (array.value) {
      for(let i = 0; i < array.value.length; i++) {
        magic += array.value[i].toString(16)
      }
    }
    return magic && magic !== '' ? magic : undefined;
  }
  catch (err) {
    //console.error('Failed to extract magic number: ', err);
    return undefined;
  }
}

@customElement('ep-blob-downloader')
export class EPBlobDownloader extends LitHauntedElement {

  // #region Input
  @property()
  fileName?: string;
  @property({ attribute: false })
  base64Contents?: string;
  @property({ attribute: false })
  dataUri?: string;
  // #endregion

  // #region Internal Output
  @property({ attribute: false })
  blob?: Blob;

  @property({ attribute: false })
  fileType?: GuessedFile;

  get isDownloadReady(): boolean {
    return this.blob !== undefined;
  }
  // #endregion

  private _handleClick() {
    if (this.blob) {
      const baseName = this.fileName ?? 'download';
      const fileName = this.fileType?.extension ? `${baseName}.${this.fileType.extension}` : `${baseName}.bin`;
      saveAs(this.blob, fileName)
    }
  }

  render() {
    useEffect(() => {
      if (this.dataUri) {
        DataURIToBlob(this.dataUri).then(blob => this.blob = blob);
      }
      else if (this.base64Contents) {
        DataURIToBlob(`data:application/octet-stream;base64,${this.base64Contents}`).then(blob => this.blob = blob);
      }
    }, [this.base64Contents, this.dataUri])

    useEffect(() => {
      if (this.blob) {
        GuessMimeType(this.blob).then(guess => this.fileType = guess);
      }
    }, [this.blob])

    return html`
      <fluent-button @click=${this._handleClick} ?disabled=${!this.isDownloadReady}>
        <span slot="start">💾</span>
        ${this.fileType?.extension ? `Download ${this.fileType.extension.toUpperCase()} file` : 'Download file'}
        <span class="file-size" slot="end">
          ${this.isDownloadReady && this.blob ? html`${formatBytes(this.blob.size)}` : ''}
        </span>
      </fluent-button>
    `
  }

  static styles = css`
    :host {
      --font-prose: ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji";
      --color-text-muted: hsla(0, 0%, 15%, 0.7);
      font-family: var(--font-prose);
    }

    .file-size {
      font-size: 0.9em;
      font-style: italic;
      color: var(--color-text-muted);
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-blob-downloader': EPBlobDownloader
  }
}
